import * as React from 'react'

import { type SelectOption } from '@factorialco/deprecated-design-system'
import { LeaveEmployeeDataFragment } from 'generated/resources/graphql'
import includes from 'lodash/includes'
import keys from 'lodash/keys'
import { reaction } from 'mobx'
import moment, { Moment } from 'moment'

import { observer } from 'mobx-react'

import FormRow from 'modules/core/components/FormRow'
import DayPicker from 'modules/core/components/Inputs/DayPicker'
import InformationTooltip from 'modules/core/components/Inputs/InformationTooltip'
import Select from 'modules/core/components/Inputs/Select'
import Text from 'modules/core/components/Inputs/Text'
import TextArea from 'modules/core/components/Inputs/TextArea'
import { dateToString, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import documents from 'modules/documents/store/collections/documents'
import type DocumentModel from 'modules/documents/store/models/Document'
import DocumentUploaderWithLinks from 'modules/timeoff/components/DocumentUploaderWithLinks'
import LeaveTypeFormInput from 'modules/timeoff/components/LeaveTypeFormInput'
import type LeaveForm from 'modules/timeoff/lib/LeaveForm'
import { getMedicalReason } from 'modules/timeoff/lib/getMedicalReason'
import { leaveFinishOn } from 'modules/timeoff/lib/leaveEmployees'
import type { LeaveTypes } from 'modules/timeoff/store/collections/leaveTypes'
import leaves from 'modules/timeoff/store/collections/leaves'
import type Allowance from 'modules/timeoff/store/models/Allowance'
import Leave from 'modules/timeoff/store/models/Leave'
import type LeaveType from 'modules/timeoff/store/models/LeaveType'
import MedicalLeaveUpdate from 'modules/timeoff/store/models/MedicalLeaveUpdate'

import MedicalLeaveTypeField from './MedicalLeaveTypeField'
import Relapse from './Relapse'

interface Props {
  form: LeaveForm
  leave?: Leave
  readOnly: boolean
  holidays: Record<string, any>
  year: number
  leaveType: LeaveType | null | undefined
  leaveTypes: LeaveTypes
  hasMedicalLeavesOpen: (employeeId: string) => boolean
  allowancesForPolicy: () => Allowance[]
  selectedLeaveType?: LeaveType
  contractStartsOn: Moment | undefined
  leaveEmployee: LeaveEmployeeDataFragment
}

const MedicalLeave: React.FC<Props> = ({
  form,
  leave,
  readOnly,
  holidays,
  year,
  leaveType,
  leaveTypes,
  hasMedicalLeavesOpen,
  allowancesForPolicy,
  selectedLeaveType,
  contractStartsOn,
  leaveEmployee,
}) => {
  /**
   * When medical leave type is `maternity` or `paternity`
   * we auto fill `effective_on` whenever `start_on` change
   * with estipulated leave time for this types
   *
   * If `effective_on` is filled we do nothing
   */
  const autoFillEffectiveOn = React.useCallback(
    (startOn: moment.Moment, type: string) => {
      const { maternity, paternity } = Leave.medicalLeaveTypes

      if (includes([paternity, maternity], type)) {
        const weeks = type === maternity ? 16 : 5
        const startOnIncluded = moment(startOn).subtract(1, 'day')
        form.setValues({
          effective_on: dateToString(startOnIncluded.add(weeks, 'weeks'), 'backend'),
        })
      } else {
        form.setValues({
          effective_on: null,
        })
      }
    },
    [form]
  )

  const document = React.useMemo((): DocumentModel | null | undefined => {
    if (!leave || !leave.has('medical_leave_document')) return null

    return documents.find({
      id: leave.attributes.medical_leave_document.id,
    })
  }, [leave])

  const canManageLeaveDocuments = React.useMemo((): boolean => {
    if (leave) return leave.policy.can('manageLeaveDocuments')

    return leaveEmployee._policies?.manageLeavesDocuments.can ?? false
  }, [leaveEmployee, leave])

  const initialDischargeOn = React.useMemo((): moment.Moment => {
    return stringToDate(form.get('start_on').value, 'ui')
  }, [form])

  const dischargeReasonsOptions = React.useMemo((): SelectOption[] => {
    return keys(MedicalLeaveUpdate.dischargeReasons).map((value) => ({
      value,
      label: getMedicalReason(value ?? ''),
    }))
  }, [])

  // Add document to collection on mount if needed
  React.useEffect(() => {
    if (
      !document &&
      leave &&
      leave.has('medical_leave_document') &&
      leave.attributes.medical_leave_document
    ) {
      documents.add([leave.attributes.medical_leave_document])
    }
  }, [document, leave])

  // Set up MobX reaction for auto-filling effective_on
  React.useEffect(() => {
    const handler = reaction(
      () => ({
        startOn: form.get('start_on').value,
        medicalLeaveType: form.get('medical_leave_type').value,
      }),
      ({ startOn, medicalLeaveType }) =>
        autoFillEffectiveOn(stringToDate(startOn, 'ui'), medicalLeaveType)
    )

    return () => {
      handler()
    }
  }, [form, autoFillEffectiveOn])

  const onDeleteDocument = React.useCallback(() => {
    if (!leave) return

    leave.set({
      document: {},
    })
  }, [leave])

  const renderBirthOnDate = () => {
    if (!form.isPaternity) return null

    return (
      <FormRow>
        <DayPicker
          field={form.get('paternity_birth_on')}
          label={i18n.t('time_off.medical_leave.birth_on')}
          readonly={!!leave}
        />
      </FormRow>
    )
  }

  const renderStartAndEndDates = () => {
    const finishOn = leave ? leaveFinishOn(leave, leaveEmployee.timeoffPolicy?.id) : null
    const readOnlyValue =
      finishOn && finishOn.isBefore(moment()) ? dateToString(finishOn, 'ui') : '-'
    return (
      <FormRow multi>
        <DayPicker
          field={form.get('start_on')}
          holidays={holidays}
          label={i18n.t('common.starting_from')}
          minDate={contractStartsOn}
          readonly={!!leave}
        />
        <DayPicker
          withNull
          withNullInitialDate={initialDischargeOn}
          withNullLabel={i18n.t('time_off.medical_leave.discharge_on.none')}
          field={form.get('effective_on')}
          holidays={holidays}
          placeholder={`(${i18n.t('common.optional')})`}
          label={i18n.t('time_off.medical_leave.discharge_on.label')}
          minDate={stringToDate(form.get('start_on').value, 'ui')}
          readonly={!!leave}
          readonlyValue={readOnlyValue}
        />
      </FormRow>
    )
  }

  const renderDischargeReason = () => {
    if (
      leave ||
      !leaves.isDischargeFull(form.get('medical_leave_type').value) ||
      !form.get('effective_on').value
    ) {
      return null
    }

    const label = i18n.t('time_off.medical_leave.discharge_reason.label')
    const field = form.get('medical_discharge_reason')

    return (
      <FormRow>
        <Select
          none={i18n.t('common.none')}
          label={label}
          field={field}
          options={dischargeReasonsOptions}
        />
      </FormRow>
    )
  }

  const renderRelapse = () => {
    if (!form.isAccident && !form.isSickness) return null

    return (
      <FormRow>
        <Relapse leaveEmployee={leaveEmployee} form={form} year={year} readOnly={readOnly} />
      </FormRow>
    )
  }

  const renderColegiateField = () => {
    const accident = form.isAccident
    const sickness = form.isSickness
    const isSicknessOrAccident = accident || sickness

    if (!isSicknessOrAccident) return null

    return (
      <FormRow multi={accident || !leave}>
        {accident && (
          <DayPicker
            field={form.get('accident_on')}
            label={i18n.t('time_off.medical_leave.accident_on.label')}
            maxDate={stringToDate(form.get('start_on').value, 'ui').add(1, 'days')}
            readonly={!!leave}
          />
        )}

        {!leave && (
          <Text
            field={form.get('colegiate_number')}
            label={i18n.t('time_off.medical_leave.colegiate_number.label')}
            secondaryLabel={
              <InformationTooltip
                information={i18n.t('time_off.medical_leave.colegiate_number.information')}
                icon="question"
              />
            }
          />
        )}
      </FormRow>
    )
  }

  const renderDocumentSection = () => {
    const description =
      leaveType && leaveType.attributes.is_attachment_mandatory
        ? i18n.t('time_off.leave_document_mandatory')
        : i18n.t('time_off.leave_document')

    return (
      <FormRow>
        <DocumentUploaderWithLinks
          document={document}
          field={form.get('medical_leave_document')}
          onDeleteDocument={onDeleteDocument}
          placeholder={description}
          readOnly={readOnly || !!leave || !canManageLeaveDocuments}
        />
      </FormRow>
    )
  }

  const isMandatory = selectedLeaveType?.attributes.details_required

  return (
    <>
      <LeaveTypeFormInput
        form={form}
        leave={leave}
        availableLeaveTypes={leaveTypes.models}
        readOnly={readOnly}
        hasMedicalLeavesOpen={hasMedicalLeavesOpen}
        selectedLeaveType={selectedLeaveType}
        leaveEmployee={leaveEmployee}
      />
      <FormRow>
        <MedicalLeaveTypeField leave={leave} form={form} />
      </FormRow>
      {renderBirthOnDate()}
      {renderStartAndEndDates()}
      {renderDischargeReason()}
      <FormRow>{renderColegiateField()}</FormRow>
      <FormRow>
        <TextArea
          maxLength={250}
          readonly={readOnly || !!leave}
          label={i18n.t('time_off.description')}
          field={form.get('description')}
          mandatory={isMandatory}
        />
      </FormRow>
      {renderDocumentSection()}
      {renderRelapse()}
    </>
  )
}

export default observer(MedicalLeave)
